// src/components/common/search/SearchTextField.tsx

import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material/TextField';

/**
 * SearchTextField
 *
 * A lightweight wrapper around MUI TextField for search UIs.
 * - Optional clear (X) button
 * - Optional `onChangeValue` convenience callback (string)
 *
 * Notes:
 * - Designed to work with `Autocomplete.renderInput(params => <SearchTextField {...params} />)`.
 * - We keep existing behavior: the clear button is shown when `value` is non-empty
 *   (or when `showClearWhenEmpty` is true).
 */
export type SearchTextFieldProps = TextFieldProps & {
  /** Convenience callback to receive the input value as a string. */
  onChangeValue?: (value: string) => void;

  /** Called when the clear (X) button is clicked. */
  onClear?: () => void;

  /** When true, show the clear button even if the value is empty. Default: false. */
  showClearWhenEmpty?: boolean;
};

const SearchTextField: React.FC<SearchTextFieldProps> = ({
  onChangeValue,
  onClear,
  showClearWhenEmpty = false,
  InputProps,
  value,
  onChange,
  ...rest
}) => {
  const hasValue =
    showClearWhenEmpty ||
    (typeof value === 'string'
      ? value.trim().length > 0
      : value !== null && value !== undefined);

  return (
    <TextField
      {...rest}
      value={value}
      onChange={(e) => {
        // Keep existing behavior: forward the event to the provided onChange handler
        // (e.g., from Autocomplete's params.inputProps.onChange).
        onChange?.(e);

        // Convenience callback for the raw string value.
        onChangeValue?.(e.target.value);
      }}
      InputProps={{
        ...InputProps,
        endAdornment: (
          <>
            {onClear && hasValue ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear"
                  edge="end"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClear();
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null}
            {InputProps?.endAdornment}
          </>
        ),
      }}
    />
  );
};

export default SearchTextField;
